
import { Button } from "@/components/ui/button";
import { Calendar, ChevronLeft, ChevronRight, Lock, CheckCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";

interface DevotionalNavigationProps {
  currentDay: number;
  isCompleted: boolean;
  onNavigate: (direction: 'prev' | 'next') => void;
  canAccessNextDay: boolean;
  isFirstDay: boolean;
  isLastDay: boolean;
}

export function DevotionalNavigation({
  currentDay,
  isCompleted,
  onNavigate,
  canAccessNextDay,
  isFirstDay,
  isLastDay,
}: DevotionalNavigationProps) {
  const isMobile = useIsMobile();

  return (
    <TooltipProvider>
      <div className="flex justify-between items-center mb-6 md:mb-8 max-w-xl mx-auto bg-white rounded-lg shadow-sm p-2 md:p-4">
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Button
                variant="ghost"
                onClick={() => onNavigate('prev')}
                disabled={isFirstDay}
                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 md:px-4"
                size={isMobile ? "sm" : "default"}
              >
                <ChevronLeft className="h-4 w-4 md:mr-1" />
                {!isMobile && "Previous"}
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {isFirstDay ? "This is the first day" : "Go to previous day"}
          </TooltipContent>
        </Tooltip>
        
        <div className="flex items-center gap-1 md:gap-2">
          <div className="flex items-center font-medium text-blue-800 text-sm md:text-base">
            <Calendar className="h-4 w-4 md:h-5 md:w-5 mr-2" />
            <span>Day {currentDay}</span>
          </div>
          {isCompleted && (
            <CheckCircle className="h-4 w-4 text-green-500" />
          )}
        </div>

        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Button
                variant="ghost"
                onClick={() => onNavigate('next')}
                disabled={isLastDay || !canAccessNextDay}
                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 md:px-4"
                size={isMobile ? "sm" : "default"}
              >
                {!isMobile && "Next"}
                {!canAccessNextDay ? (
                  <Lock className="h-4 w-4 md:ml-1" />
                ) : (
                  <ChevronRight className="h-4 w-4 md:ml-1" />
                )}
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {!canAccessNextDay
              ? "Complete this day to unlock the next one"
              : "Continue to the next day"}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
